import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Card,
  CardContent,
  Box,
  Button,
  Typography,
  TableContainer,
  InputAdornment,
  TextField,
  CircularProgress,
  Chip,
  Stack,
  Badge,
  Tooltip,
  Avatar,
  TablePagination,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  FiberManualRecord as DotIcon,
  Storefront,
} from '@mui/icons-material';
import { request } from '../../../../../utils/request';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDebounce } from '../../../../../utils/useDebounce';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';

import DeleteConfirmDialog from '../../../../../componentsUiBlock/DeleteConfirmDialog';
import { useDeleteDialog } from '../../../../../utils/useDeleteDialog';
import { BrandDetail, Vendor } from '../../../types';
import { usePermissions } from '../../../../../utils/usePermissions';

interface HeadCell {
  id: keyof Vendor | 'action' | 'contactInfo' | 'brandsInfo' | 'locationInfo';
  label: string;
  sortable?: boolean;
}

const headCells: HeadCell[] = [
  { id: 'vendorName', label: 'Vendor', sortable: true },
  { id: 'type', label: 'Type', sortable: true },
  { id: 'contactInfo', label: 'Contact', sortable: false },
  { id: 'locationInfo', label: 'Location', sortable: true },
  { id: 'brandsInfo', label: 'Brands', sortable: false },
  { id: 'gstNumber', label: 'GST Number', sortable: true },
  { id: 'action', label: 'Action', sortable: false }
];

const VendorList: React.FC = () => {
  const { canCreateVendors, canDeleteVendors } = usePermissions();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof Vendor | 'brandsCount'>('vendorName');
  const [search, setSearch] = useState('');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const navigate = useNavigate();
  const debouncedSearch = useDebounce(search, 500);



  useEffect(() => {
    const getVendorData = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          page: (page + 1).toString(),
          limit: rowsPerPage.toString(),
        });

        if (debouncedSearch) {
          params.append('q', debouncedSearch);
        }

        const url = `vendor/get-all-vendors?${params.toString()}`;

        const res = await request({
          url: url,
          method: 'get',
        });
        if (res.vendors) {
          setVendors(res.vendors);
          setTotal(res.total);
        }
      } catch (error: any) {
        console.error(error);
        toast.error(error || 'Failed to fetch vendors');
      } finally {
        setLoading(false);
      }
    };
    getVendorData();
  }, [debouncedSearch, page, rowsPerPage]);

  const handleSort = (property: HeadCell['id']) => {
    if (!headCells.find(cell => cell.id === property)?.sortable) return;

    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property as keyof Vendor | 'brandsCount');
  };

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  const sortedVendors = [...vendors].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    if (orderBy === 'brandsCount') {
      aValue = a.brands.length;
      bValue = b.brands.length;
    } else {
      aValue = a[orderBy as keyof Vendor];
      bValue = b[orderBy as keyof Vendor];
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return order === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return order === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return 0;
  });

  const deleteVendor = async (vendor: Vendor) => {
    try {
      const res = await request({
        url: `vendor/delete-vendor/${vendor._id}`,
        method: "delete",
      });
      if (res?.status === "success") {
        toast.success(res?.message);
        // Remove the vendor from the list
        setVendors(prev => prev.filter(vd => vd._id !== vendor._id));

        // If we deleted the last item on the page, go to previous page
        if (vendors.length === 1 && page > 0) {
          setPage(page - 1);
        }
      } else {
        toast.error(res?.message);
      }
    } catch (error: any) {
      toast.error(error)
    }

  }

  const { open, loadingDelete, itemToDelete, openDialog, closeDialog, confirmDelete } = useDeleteDialog<Vendor>({
    onDelete: deleteVendor,
  })

  // Function to render brand chips with logos
  const renderBrandChips = (brands: BrandDetail[], maxDisplay = 2) => {
    const displayBrands = brands.slice(0, maxDisplay);
    const remainingCount = brands.length - maxDisplay;

    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
        {displayBrands.map((brand, index) => (
          <Tooltip key={index} title={brand.brandName} arrow>
            <Chip
              avatar={
                brand.brandLogo ? (
                  <Avatar
                    src={brand.brandLogo}
                    alt={brand.brandName}
                    sx={{ width: 20, height: 20 }}
                  />
                ) : (
                  <Avatar sx={{ width: 20, height: 20, bgcolor: 'primary.main', fontSize: '0.7rem' }}>
                    {brand.brandName?.charAt(0).toUpperCase()}
                  </Avatar>
                )
              }
              label={brand.brandName}
              variant="filled"
              size="small"
              sx={{
                fontSize: '0.65rem',
                height: 24,
                mb: 0.5,
                maxWidth: 120,
                '& .MuiChip-label': {
                  px: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }
              }}
            />
          </Tooltip>
        ))}
        {remainingCount > 0 && (
          <Tooltip
            title={brands.slice(maxDisplay).map(brand => brand.brandName).join(', ')}
            arrow
          >
            <Chip
              label={`+${remainingCount}`}
              variant="outlined"
              size="small"
              sx={{
                fontSize: '0.65rem',
                height: 24,
                '& .MuiChip-label': {
                  px: 0.5,
                }
              }}
            />
          </Tooltip>
        )}
      </Stack>
    );
  };

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
        <Brandcrumb />
      </Box>
      <Card elevation={4}>
        <CardContent sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            {canCreateVendors() && (
              <Button
                variant="contained"
                size='small'
                color="primary"
                onClick={() => navigate("/vendors/vendor/addVendor")}
                sx={{ flexShrink: 0 }}
                startIcon={<Storefront />}
              >
                Add New Vendor
              </Button>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={3}>
              <Badge badgeContent={total} showZero color="primary">
                <Storefront color="action" />
              </Badge>

              <TextField
                size="small"
                placeholder="Search vendors..."
                variant="outlined"
                sx={{ width: { xs: '100%', sm: 300 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
          </Box>
        </CardContent>

        <CardContent sx={{ pt: 1 }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {headCells.map((head) => (
                    <TableCell key={head.id} sx={{ fontWeight: 'bold' }}>
                      {head.sortable !== false ? (
                        <TableSortLabel
                          active={orderBy === head.id}
                          direction={orderBy === head.id ? order : 'asc'}
                          onClick={() => handleSort(head.id)}
                        >
                          {head.label}
                        </TableSortLabel>
                      ) : (
                        head.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {debouncedSearch ? 'Searching...' : 'Loading vendors...'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : sortedVendors.length > 0 ? (
                  sortedVendors.map((vendor) => (
                    <TableRow key={vendor._id} hover>
                      {/* Vendor Name with Status Dot and Nickname */}
                      <TableCell>
                        <Box display="flex" alignItems="flex-start" gap={1}>
                          <Tooltip
                            title={vendor.status === 'active' ? 'Active Vendor' : 'Inactive Vendor'}
                            arrow
                            placement="top-start"
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                              <DotIcon
                                sx={{
                                  color: vendor.status === 'active' ? 'success.main' : 'error.main',
                                  fontSize: 16,
                                }}
                              />
                            </Box>
                          </Tooltip>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {vendor.vendorName}
                            </Typography>
                            {vendor.nickName && (
                              <Typography variant="caption" color="text.secondary">
                                ({vendor.nickName})
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Type as Chips */}
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {vendor.type.slice(0, 2).map((type, index) => (
                            <Chip
                              key={index}
                              label={type}
                              color="primary"
                              variant="outlined"
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                height: 20,
                                mb: 0.5,
                                '& .MuiChip-label': {
                                  px: 0.8,
                                }
                              }}
                            />
                          ))}
                          {vendor.type.length > 2 && (
                            <Chip
                              label={`+${vendor.type.length - 2}`}
                              variant="filled"
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                height: 20,
                                '& .MuiChip-label': {
                                  px: 0.8,
                                }
                              }}
                            />
                          )}
                        </Stack>
                      </TableCell>

                      {/* Contact Info - Single Phone and Email */}
                      <TableCell>
                        <Box sx={{ maxWidth: 150 }}>
                          {/* Primary Phone */}
                          {vendor.contact.phone.length > 0 && (
                            <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" noWrap title={vendor.contact.phone[0].phoneNo}>
                                {vendor.contact.phone[0].phoneNo}
                              </Typography>
                            </Box>
                          )}

                          {/* Primary Email */}
                          {vendor.contact.email.length > 0 && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" noWrap title={vendor.contact.email[0]}>
                                {vendor.contact.email[0]}
                              </Typography>
                            </Box>
                          )}

                          {/* Show counts if more available */}
                          {(vendor.contact.phone.length > 1 || vendor.contact.email.length > 1) && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              +{vendor.contact.phone.length - 1} phone{vendor.contact.phone.length > 2 ? 's' : ''},
                              +{vendor.contact.email.length - 1} email{vendor.contact.email.length > 2 ? 's' : ''}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      {/* Location with City */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                          <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="body2" noWrap>
                            {vendor.location}
                          </Typography>
                        </Box>
                        {vendor.address.city && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {vendor.address.city}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Brands - Show with logos */}
                      <TableCell>
                        {renderBrandChips(vendor.brands, 2)}
                        <Typography variant="caption" color="text.secondary">
                          {vendor.brands.length} brand{vendor.brands.length !== 1 ? 's' : ''}
                        </Typography>
                      </TableCell>

                      {/* GST Number */}
                      <TableCell>
                        {vendor.gstNumber ? (
                          <Typography variant="body2" fontFamily="monospace" fontSize="0.8rem">
                            {vendor.gstNumber}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Not provided
                          </Typography>
                        )}
                      </TableCell>

                      {/* Action Buttons */}
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.8}>
                          <Button
                            variant="contained"
                            color='secondary'
                            onClick={() => navigate(`/vendors/vendor/dailyLedger/${vendor._id}`)}
                            sx={{
                              minWidth: 'auto',
                              fontSize: '0.60rem',
                              textWrap: 'nowrap',
                              py: 0.1,
                              px: 2,
                              borderRadius: 5,
                              border: '.8px solid #7f5ee666',
                              '&:hover': { backgroundColor: '#7f5ee666' }
                            }}
                          >
                            Daily Ledger
                          </Button>
                          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap={0.8}>
                            <Button
                              variant="text"
                              fullWidth
                              onClick={() => navigate(`/vendors/vendor/${vendor._id}`)}
                              sx={{
                                minWidth: 'auto',
                                fontSize: '0.60rem',
                                textWrap: 'nowrap',
                                py: 0.1,
                                px: 2,
                                borderRadius: 5,
                                border: '.8px solid #7f5ee666',
                                '&:hover': { backgroundColor: '#7f5ee666' }
                              }}
                            >
                              View / Edit
                            </Button>
                            {canDeleteVendors() && (
                              <Button
                                size="small"
                                fullWidth
                                variant="text"
                                color="error"
                                onClick={() => openDialog(vendor)}
                                sx={{
                                  minWidth: 'auto',
                                  fontSize: '0.60rem',
                                  py: 0.1,
                                  px: 2,
                                  borderRadius: 5,
                                  border: '.8px solid #ff4d6e5e',
                                  '&:hover': { backgroundColor: '#ff4d6e5e' }
                                }}
                              >
                                Delete
                              </Button>
                            )}

                          </Box>

                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        {search ? `No vendors found for "${search}"` : 'No vendors available. Add your first vendor!'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Component */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              '.MuiTablePagination-toolbar': {
                padding: 1,
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={open}
        loading={loadingDelete}
        title='Delete Vendor'
        description={
          <>
            Are you sure you want to delete the vendor <strong>"{itemToDelete?.vendorName}"</strong>? <br /><br />
            <Alert severity="warning" sx={{ fontSize: "12px", py: "0px" }}>This action cannot be undone and all associated data will be permanently removed.</Alert>
          </>
        }
        onCancel={closeDialog}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default VendorList;